/**
 * Supabase client configuration
 * Provides a typed Supabase client for use throughout the application
 * With enhanced _error handling and connection validation
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { /* unused */ env } from '../config/env';
import type { Database } from '@/types/supabase';

// Re-export Json type from supabase types
export type { Json } from '@/types/supabase';

// Extract project ID from Supabase URL for correct storage key format
const getProjectId = (url: string): string => {
  try {
    const match = url.match(/https:\/\/([^.]+)\.supabase\.co/);
    return match ? match[1] : 'default';
  } catch {
    return 'default';
  }
};

const projectId = getProjectId(env.supabase.url);
const storageKey = `sb-${projectId}-auth-token`;

// Connection configuration with proper CORS, timeouts, and improved resiliency
const clientOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey, // Use Supabase standard format: sb-{projectId}-auth-token
    storage: {
      getItem: (key: string): string | null => {
        try {
          const item = localStorage.getItem(key);
          if (process.env.NODE_ENV === 'development') {

          }
          return item;
        } catch (e) {

          return null;
        }
      },
      setItem: (key: string, value: string): void => {
        try {
          localStorage.setItem(key, value);
          if (process.env.NODE_ENV === 'development') {

          }
        } catch (e) {

        }
      },
      removeItem: (key: string): void => {
        try {
          localStorage.removeItem(key);
          if (process.env.NODE_ENV === 'development') {

          }
        } catch (e) {

        }
      }
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'bucket-react@1.0.0',
    },
  },
  // Enhanced realtime configuration for persistent connections
  realtime: {
    // Use Web Worker for realtime to prevent background tab throttling
    worker: true,
    // Keep the socket alive even behind proxies
    heartbeatIntervalMs: 10000, // 10 seconds heartbeat (more aggressive)
    // Wait longer before giving up & more reconnection attempts
    maxReconnectAttempts: 20, // Increase reconnect attempts for better resilience
    timeout: 90000, // 90 seconds timeout (more lenient)
    // Exponential backoff for reconnection attempts
    backoff: {
      maxDelay: 15000, // Max delay between reconnect attempts (15s)
      minDelay: 1000,  // Min delay (1s)
      factor: 1.5      // Exponential factor
    },
    // Debug event handlers
    debug: process.env.NODE_ENV === 'development',
  },
  persistSession: true, // Top-level persistence for backward compatibility
};

// Log the connection parameters (without exposing the full anon key for security)
const logConnectionDetails = () => {
  // Only log the first 8 characters of the key for security
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const __partialKey = env.supabase.anonKey.substring(0, 8) + '...[REDACTED]';




};

// In development mode, log connection details
if (process.env.NODE_ENV === 'development') {
  logConnectionDetails();
}

// Track connection status for app-wide visibility
class ConnectionTracker {
  private static instance: ConnectionTracker;
  private lastEventTime = Date.now();
  private disconnectCount = 0;
  private reconnectCount = 0;
  private clientInitTime = Date.now();
  
  private constructor() {}
  
  public static getInstance(): ConnectionTracker {
    if (!ConnectionTracker.instance) {
      ConnectionTracker.instance = new ConnectionTracker();
    }
    return ConnectionTracker.instance;
  }
  
   
  public recordDisconnect(reason: string): void {
    this.disconnectCount++;
    this.lastEventTime = Date.now();

  }
  
  public recordReconnect(): void {
    this.reconnectCount++;
    this.lastEventTime = Date.now();

  }
  
  public getStats(): { disconnects: number; reconnects: number; uptime: number; lastEvent: Date } {
    return {
      disconnects: this.disconnectCount,
      reconnects: this.reconnectCount,
      uptime: Date.now() - this.clientInitTime,
      lastEvent: new Date(this.lastEventTime)
    };
  }
}

export const connectionTracker = ConnectionTracker.getInstance();

// Create a typed Supabase client with enhanced options
export const supabase: SupabaseClient<Database> = createClient<Database>(
  env.supabase.url,
  env.supabase.anonKey,
  clientOptions
);

// Initialize connection on load
(async function initializeConnection() {
  if (typeof window !== 'undefined') {
    try {
      // Log the connection parameters for debugging
      
      
      // Verify that the Supabase URL and anon key are valid
      if (!env.supabase.url || !env.supabase.anonKey) {

        return;
      }
      
      // Get the current session if any
      const { data } = await supabase.auth.getSession();
      if (data.session) {

      } else {

      }
    } catch (e) {

    }
  }
})();

// Add event listeners for auth state changes to help with debugging
 
supabase.auth.onAuthStateChange((event, session) => {
  

  // If we detect a SIGNED_OUT event but localStorage still has a session,
  // this could indicate a token sync issue. Try to recover.
  if (event === 'SIGNED_OUT' && localStorage.getItem('bucket-supabase-auth')) {

    // Don't forcibly refresh here to avoid loops, but flag it for attention
  }

  // If we detect a token refresh, log it for debugging
  if (event === 'TOKEN_REFRESHED') {

  }
});

// Add periodic token refresh to prevent expiration
// This helps ensure we always have a valid token for operations
setInterval(async () => {
  try {
    // Only refresh if we have a session
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const { error } = await supabase.auth.refreshSession();
      if (error) {

      } else {

      }
    }
  } catch (e) {

  }
}, 10 * 60 * 1000); // Refresh every 10 minutes

// Listen for auth and websocket events to track connection status
if (typeof window !== 'undefined') { // Only run in browser
  // Auth state changes
   
  supabase.auth.onAuthStateChange((event, session) => {
    if (process.env.NODE_ENV === 'development') {

    }
    
    if (event === 'SIGNED_OUT') {
      connectionTracker.recordDisconnect('Auth: ' + event);
    } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      connectionTracker.recordReconnect();
    }
  });

  // Track realtime status changes if available
  const realtime = supabase.channel('system');
  realtime
    .on('system', { event: 'disconnect' }, () => {
      connectionTracker.recordDisconnect('Realtime disconnect');
    })
    .on('system', { event: 'reconnect' }, () => {
      connectionTracker.recordReconnect();
    })
    .subscribe();
}

/**
 * Verifies the connection to Supabase by attempting a simple query
 * @returns boolean indicating if the connection was successful
 */
/**
 * Advanced verification function that checks connection, token validity, and _data access
 * Returns detailed information about the connection state
 */
export async function verifyConnection(): Promise<boolean> {
  const results = await verifyConnectionDetailed();
  return results.connected;
}

/**
 * Detailed connection verification with multi-stage checks
 * Helps diagnose exactly where connection issues might be occurring
 */
export async function verifyConnectionDetailed(): Promise<{
  connected: boolean;
  networkConnected: boolean;
  tokenValid: boolean;
  dataAccessible: boolean;
  message: string;
}> {
  const result = {
    connected: false,
    networkConnected: false,
    tokenValid: false,
    dataAccessible: false,
    message: ''
  };

  // Network connectivity check
  try {

    const controller = new AbortController();
    // 15 second timeout, more generous for slower connections
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    // First try with default mode
    try {
      const healthUrl = `${env.supabase.url}/auth/v1/health`;

      const resp = await fetch(healthUrl, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache',
        headers: {
          'apikey': env.supabase.anonKey,
          'Authorization': `Bearer ${env.supabase.anonKey}`
        }
      });

      // Treat 200 (OK) as success, but also treat 401 (Unauthorized) or 404 (Not Found)
      // as evidence that the host is reachable even if the endpoint requires auth.
      if (resp.ok || resp.status === 401 || resp.status === 404) {

        clearTimeout(timeoutId);

        result.networkConnected = true;
        return result;
      } else {

      }
    } catch (healthError) {

      // Continue to fallback check
    }

    // As fallback, use no-cors mode which always succeeds if the server exists

    try {
      // The ping endpoint is more reliable than the root URL
      // Even with 404 status, if we get any response it means network is up
      await fetch(`${env.supabase.url}/ping`, {
        method: 'GET',
        mode: 'no-cors', // This will always succeed if the server is reachable
        signal: controller.signal,
        cache: 'no-cache'
      });
    } catch (pingError) {
      // If ping fails, try root as last resort

      await fetch(env.supabase.url, {
        method: 'HEAD',
        mode: 'no-cors', // This will always succeed if the server is reachable
        signal: controller.signal,
        cache: 'no-cache'
      });
    }
    
    clearTimeout(timeoutId);

    result.networkConnected = true;
  } catch (_error: unknown) {
    // Type-safe _error handling
    const netErr = _error instanceof Error ? _error as Error : new Error(String(_error));
    
    // Handle specific error types with type assertions
    if ((netErr as Error).name === 'AbortError') {

      result.message = 'Network timeout - connection too slow';
    } else if ((netErr as Error).name === 'TypeError' && (netErr as Error).message.includes('Failed to fetch')) {

      result.message = 'Network connectivity issue - check internet connection';
    }
    
    // Fast fail if network is down
    return result;
  }
    
  // Session verification to validate JWT token
  try {
    try {
      const { data: sessionData, error: sessionError  } = await supabase.auth.getSession();
      
      if (sessionError) {

        result.message = 'Authentication session invalid or expired';
      } else if (!sessionData.session) {

        result.message = 'No active session found';
      } else {
        // We have a valid token/session

        result.tokenValid = true;
        
        // Force token refresh to ensure it's absolutely current
        try {
          // First check if token is actually close to expiry
          const _session = sessionData.session;
          const tokenExpiry = _session?.expires_at ? new Date(_session.expires_at * 1000) : null;
          const now = new Date();
          const timeUntilExpiry = tokenExpiry ? tokenExpiry.getTime() - now.getTime() : 0;
          
          // If token expires in less than 10 minutes or if no expiry info, refresh it
          if (!tokenExpiry || timeUntilExpiry < 10 * 60 * 1000) {

            const { error: _refreshError  } = await supabase.auth.refreshSession();
            if (_refreshError) {

            } else {

            }
          } else {

          }
        } catch (refreshErr) {

          // Don't fail the whole check just because refresh failed
        }
      }
    } catch (sessionErr) {

      // Continue to _data access checks even if session check fails
    }
    
    // Attempt _data access checks even if token seems invalid
    // as RLS might still allow some queries
    
    // Try multiple tables for more reliable detection
    const checkTables = async () => {
      // First try group_sessions
      const { error: _sessionError  } = 
        await supabase.from('group_sessions').select('count').limit(1);
        
      if (!_sessionError) {

        return true;
      }

      const { error: profileError  } = 
        await supabase.from('profiles').select('id').limit(1);
        
      if (!profileError) {

        return true;
      }
      
      // Both checks failed



      return false;
    };
    
    // Run the table checks
    result.dataAccessible = await checkTables();
    
    // Update connected status - be more lenient, the network check is the most important
    result.connected = result.networkConnected && 
                       // If network is reachable, that's enough for basic connectivity
                       // We don't require token validity or _data access for the connection indicator
                       true;
    
    if (result.connected) {
      result.message = 'Connection verified successfully';
      // Record the successful check
      if (typeof window !== 'undefined') {
        connectionTracker.recordReconnect();
      }
    } else if (!result.message) {
      // Set a message if none was set earlier
      result.message = 'Connection failed - see console for details';
    }
    
    // Even if _data access fails but network is up, mark as connected for better UX
    if (result.networkConnected && !result.connected) {

      result.connected = true;
      result.message = 'Limited connectivity (network only)'; 
    }
    
    return result;
  } catch (err) {
    // Enhanced _error logging with more diagnostics
    const _error = err instanceof Error ? err as Error : new Error(String(err));
    
    
    
    // Record the disconnect event
    if (typeof window !== 'undefined') {
      connectionTracker.recordDisconnect(_error.message);
    }
    
    // Update result with _error info
    result.message = `Unexpected error: ${_error.message}`;
    
    // Development-only extra logging
    if (process.env.NODE_ENV === 'development') {


    }
    
    return result;
  }
};

// Export the client as the default export
export default supabase;
